// Doc 07 §6 — Template detail page: builds the same editor stores as the trip page
// parameterised by TemplateId as the listId (trips and templates are both lists),
// then renders the TemplateEditor.

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { vars } from "@pack-it-up/ui-web";
import { asTemplateId, ok } from "@pack-it-up/shared";
import type { DomainError, Result, UserId } from "@pack-it-up/shared";
import {
  createCategoriesStore,
  createItemsStore,
  createNotesStore,
  createTemplatesStore,
  createTodosStore,
} from "@pack-it-up/viewmodel";
import { useStoreValue } from "@pack-it-up/viewmodel/bindings";
import { useStores } from "../../app/AppContext.js";
import { useSession } from "../../app/SessionProvider.js";
import { useSessionDeps } from "../../app/sessionDeps.js";
import { CopyDialog } from "../shared/CopyDialog.js";
import { TemplateEditor } from "./TemplateEditor.js";
import { copyName, type AllRepositories, type ParticipantRemovalPlan } from "@pack-it-up/core";

export const TemplateDetailPage = (): ReactNode => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { auth } = useStores();
  const { repos } = useSessionDeps();
  const { home, copy } = useSession();
  const copyBusy = useStoreValue(copy, (s) => s.busy);
  const copyError = useStoreValue(copy, (s) => s.error);
  const [duplicating, setDuplicating] = useState(false);

  const actor = useStoreValue(auth, (s) => s.user?.id ?? null) as UserId | null;
  const templates = useStoreValue(home, (s) => s.templates);
  const template = templates.find((t) => t.id === id);

  const templateId = id !== undefined ? asTemplateId(id) : null;

  // Build editor stores with templateId as the listId (Doc 07 §2: "categories/items/
  // todos inside a template reuse the Category/Item/Todo repos with a TemplateId
  // parentId").
  const stores = useMemo(() => {
    if (templateId === null || actor === null) return null;
    return {
      categories: createCategoriesStore({ categories: repos.categories, parentId: templateId, actor }),
      items: createItemsStore({ items: repos.items, listId: templateId, actor }),
      todos: createTodosStore({ todos: repos.todos, listId: templateId, actor }),
      notes: createNotesStore({ notes: repos.notes, listId: templateId, actor }),
      templates: createTemplatesStore({
        templates: repos.templates,
        userId: actor,
        // Issue #185 — same seam shape for the row cleanup behind removing a traveler.
        // Both backends implement this one, so absence would be a wiring bug; it still
        // degrades to "removed the traveler, left the rows" rather than failing.
        applyParticipantRemovalPlan: async (plan) => {
          const seam = repos as AllRepositories & {
            applyParticipantRemovalPlan?: (p: ParticipantRemovalPlan) => Promise<Result<void, DomainError>>;
          };
          return (await seam.applyParticipantRemovalPlan?.(plan)) ?? ok(undefined);
        },
      }),
    };
  }, [repos, actor, templateId]);

  // Each store owns its Firestore streams; a superseded bundle (the route's template
  // changed, the user changed, or the repos seam was swapped) must unsubscribe or its
  // listeners keep firing into state nothing renders. Deliberately not an effect
  // *cleanup*: StrictMode's simulated remount runs cleanups without rebuilding the memo,
  // which would leave the live bundle disposed. Disposing only the previous set is
  // remount-safe.
  const previous = useRef<typeof stores>(null);
  useEffect(() => {
    const superseded = previous.current;
    previous.current = stores;
    if (superseded !== null && superseded !== stores) {
      superseded.categories.getState().dispose();
      superseded.items.getState().dispose();
      superseded.todos.getState().dispose();
      superseded.notes.getState().dispose();
      superseded.templates.getState().dispose();
    }
  }, [stores]);

  if (templateId === null || stores === null || template === undefined) {
    return (
      <div style={{ padding: vars.space.xl, color: vars.color.textMuted }}>
        {templateId === null ? "Invalid template URL." : "Loading…"}
      </div>
    );
  }

  // Issue #8 — a template shared read-only with you is copied, not edited: the new
  // template is created under your account with no tie back to the original.
  const duplicate = async (name: string): Promise<void> => {
    const r = await copy.getState().duplicateTemplate(templateId, name);
    if (!r.ok) return; // the dialog stays open showing the error
    setDuplicating(false);
    navigate(`/template/${r.value}`);
  };

  return (
    <>
      <TemplateEditor
        template={template}
        categoriesStore={stores.categories}
        itemsStore={stores.items}
        todosStore={stores.todos}
        notesStore={stores.notes}
        templatesStore={stores.templates}
        onDeleted={() => navigate("/templates")}
        onDuplicate={() => setDuplicating(true)}
      />
      <CopyDialog
        open={duplicating}
        title="Duplicate template"
        subtitle="Creates your own copy — categories, items and to-dos included."
        label="Template name"
        defaultName={copyName(template.name)}
        confirmLabel="Duplicate"
        busy={copyBusy}
        error={copyError}
        onClose={() => setDuplicating(false)}
        onConfirm={(name) => void duplicate(name)}
      />
    </>
  );
};
